
import { useState, useRef, memo, useCallback, useEffect } from 'react'
import { ChatMessages } from '../ChatMessages'
import { ChatInput } from '../ChatInput'
import { useChatContext } from '../../../../contexts/ChatContext'
import { useChatMessages } from '../../hooks/useChatMessages'
import { useChatFiles } from '../../hooks/useChatFiles'
import { useUser } from '../../../../contexts/UserContext'
import { useCurrentAgent } from '../../hooks/useCurrentAgent'
import { useChatConversations } from '../../hooks/useChatConversations'
import { useToast } from '../../../../contexts/ToastContext'

/**
 * ChatView - Presentational Component
 * 
 * Consumes ChatContext for all chat data and actions.
 * No direct business hook calls.
 */

interface ChatViewProps {
    // UI Configuration
    isFullscreen: boolean
}

export const ChatView = memo(({
    isFullscreen,
}: ChatViewProps) => {
    // ===== CONTEXT (Single Source of Truth) =====
    // Use Context for stable Identifiers only (Phase 4.1)
    const { currentAgentId, currentAgentName, copyMessage } = useChatContext()
    const { user } = useUser()
    const userId = user?.id || null

    // Selectors & Base Data Hooks
    // Phase 4.3: Ensure Domain Hooks are mounted to trigger loading
    const {
        currentConversationId: storeConvId,
        isLoading: isLoadingConvs,
        isLoaded: isConvsLoaded,
        createConversation // NEW: Needed for "New Chat" bootstrap
    } = useChatConversations(userId, currentAgentId)

    // Derived selector for stability
    const conversationId = storeConvId || null

    const { agent } = useCurrentAgent(currentAgentId, null)
    const agentAvatar = agent?.avatar_url || null

    // Atomic Hooks: No more monolithic re-renders
    const { loadError, hasAttemptedLoad, sendMessage: hookSendMessage, sendFeedback, messages, isLoading: isLoadingMessages } = useChatMessages(currentAgentId, conversationId, userId)
    const { attachedFiles, processAndAttachFiles, removeFile } = useChatFiles(currentAgentId)

    // ===== LOCAL UI STATE =====
    const [inputMessage, setInputMessage] = useState('')
    const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null)
    const [isBootstrapping, setIsBootstrapping] = useState(false)
    const { showToast } = useToast()
    const realFileInputRef = useRef<HTMLInputElement>(null)

    // Stabilize input value for callbacks without re-triggering memoized children
    const inputRef = useRef(inputMessage)
    useEffect(() => { inputRef.current = inputMessage }, [inputMessage])

    const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (!files || files.length === 0) return
        await processAndAttachFiles(files)
        e.target.value = ''
    }, [processAndAttachFiles])

    const handleSend = useCallback(async () => {
        const content = inputRef.current.trim()
        if ((!content && attachedFiles.length === 0) || !currentAgentId) return

        // 🔴 CRITICAL FIX #2: Lock to prevent race conditions during bootstrap
        if (isBootstrapping) return

        let activeConvId = conversationId
        let didBootstrap = false

        // Phase 3.2: Bootstrap conversation if starting from fresh slate (New Chat button)
        if (!activeConvId) {
            didBootstrap = true
            setIsBootstrapping(true)
            console.log('[ChatView] Creating new conversation for bootstrap...')

            // 🟢 CRITICAL FIX: Use full content (backend truncates) and handle null safely
            const newConv = await createConversation(content)

            if (!newConv) {
                // 🔴 CRITICAL FIX: User feedback on failure
                showToast('error', 'Falha ao iniciar conversa. Tente novamente.')
                setIsBootstrapping(false)
                return
            }
            activeConvId = newConv.id
        }

        setInputMessage('')

        try {
            await hookSendMessage(content, attachedFiles, activeConvId)
        } finally {
            attachedFiles.forEach(f => removeFile(f.id))
            // Only release lock if we were the ones who set it
            if (didBootstrap) {
                setIsBootstrapping(false)
            }
        }
    }, [currentAgentId, attachedFiles, hookSendMessage, conversationId, createConversation, removeFile, isBootstrapping, showToast])

    const handleCopy = useCallback(async (content: string, index: number) => {
        await copyMessage(content)
        setCopiedMessageId(index)
        setTimeout(() => setCopiedMessageId(null), 2000)
    }, [copyMessage])

    const handleFeedback = useCallback((index: number, feedback: 'liked' | 'disliked') => {
        const message = messages[index]
        if (!message) return
        const newFeedback = message.feedback === feedback ? null : feedback
        sendFeedback(message.id, newFeedback)
    }, [messages, sendFeedback])

    const handleAttachButton = useCallback(() => {
        realFileInputRef.current?.click()
    }, [])

    return (
        <div className={`flex-1 flex flex-col overflow-hidden w-full relative ${isFullscreen ? 'max-w-4xl mx-auto' : ''}`}>
            <input
                type="file"
                ref={realFileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                multiple
                accept="image/*,application/pdf,text/*"
            />

            {/* Industrial Shield: Message List Isolation */}
            <ChatMessages
                agentId={currentAgentId}
                messages={messages}
                agentName={currentAgentName || 'Chat'}
                agentAvatar={agentAvatar}
                agent={agent}
                onCopy={handleCopy}
                onFeedback={handleFeedback}
                copiedMessageId={copiedMessageId}
                hasAttemptedLoad={conversationId ? hasAttemptedLoad : isConvsLoaded}
                isLoading={isLoadingConvs || (!!conversationId && isLoadingMessages)}
                loadError={loadError}
            />

            {/* Industrial Shield: Input Area Isolation */}
            <ChatInput
                agentId={currentAgentId}
                value={inputMessage}
                onChange={setInputMessage}
                onSend={handleSend}
                attachedFiles={attachedFiles}
                onRemoveFile={removeFile}
                onAttachFile={handleAttachButton}
                isSubmitting={isBootstrapping}
            />
        </div>
    )
})
